import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Globe, Ticket, Users, ArrowUpRight } from "lucide-react";
import { resolveAsset } from "@/lib/brand";
import { toast } from "sonner";

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", domain: "", contact_name: "", contact_email: "",
    primary_color: "#0EA5E9", accent_color: "#3B82F6", plan: "Growth",
    login_email: "", login_password: "",
  });

  const load = useCallback(() => api.get("/clients").then((r) => setClients(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Company name required");
    setSaving(true);
    try {
      const { data: client } = await api.post("/clients", {
        name: form.name, domain: form.domain, contact_name: form.contact_name,
        contact_email: form.contact_email, primary_color: form.primary_color,
        accent_color: form.accent_color, plan: form.plan,
      });
      if (form.login_email && form.login_password) {
        await api.post("/users", {
          email: form.login_email, password: form.login_password,
          name: form.name, role: "client", client_id: client.id,
        });
      }
      toast.success("Client created");
      setOpen(false);
      setForm({ name: "", domain: "", contact_name: "", contact_email: "", primary_color: "#0EA5E9", accent_color: "#3B82F6", plan: "Growth", login_email: "", login_password: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Clients</h1>
          <p className="text-slate-500 text-sm mt-1">{clients.length} client {clients.length === 1 ? "company" : "companies"}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="text-white" style={{ background: "var(--cp-primary)" }} data-testid="new-client-btn"><Plus size={16} className="mr-1" /> New Client</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Create client</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Company name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="client-name-input" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Website domain</Label>
                  <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="example.com" className="mt-1.5" data-testid="client-domain-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Contact name</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Contact email</Label>
                  <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Brand color</Label>
                  <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-slate-200" data-testid="client-color-input" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Accent</Label>
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-slate-200" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Plan</Label>
                  <Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3.5 space-y-3">
                <p className="text-xs uppercase tracking-wider text-slate-500">Client login (username & password)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} placeholder="username@client.com" data-testid="client-login-email" />
                  <Input value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} placeholder="Set a password" data-testid="client-login-password" />
                </div>
                <p className="text-[11px] text-slate-400">They'll use these credentials to access their branded portal.</p>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} className="text-white" style={{ background: "var(--cp-primary)" }} data-testid="submit-client-btn">{saving ? "Creating…" : "Create Client"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clients.map((c) => (
          <Card key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid="client-card"
            className="p-5 border-slate-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
            <div className="flex items-center gap-3">
              {c.logo_url ? (
                <img src={resolveAsset(c.logo_url)} alt="" className="h-12 w-12 rounded object-contain bg-slate-50 p-1 border border-slate-100" />
              ) : (
                <div className="h-12 w-12 rounded flex items-center justify-center text-white font-display font-bold" style={{ background: c.primary_color }}>{c.name[0]}</div>
              )}
              <div className="min-w-0">
                <h3 className="font-display font-semibold text-slate-900 truncate">{c.name}</h3>
                <p className="text-xs text-slate-400 flex items-center gap-1"><Globe size={11} /> {c.domain || "—"}</p>
              </div>
              <ArrowUpRight size={16} className="ml-auto text-slate-300 group-hover:text-slate-600 transition-colors" />
            </div>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
              <span className="flex items-center gap-1.5 text-slate-500"><Ticket size={14} /> {c.open_tickets} open</span>
              <span className="flex items-center gap-1.5 text-slate-500"><Users size={14} /> {c.user_count} user{c.user_count === 1 ? "" : "s"}</span>
              <span className="ml-auto text-[11px] px-2 py-0.5 rounded" style={{ background: `${c.primary_color}1a`, color: c.primary_color }}>{c.plan}</span>
            </div>
          </Card>
        ))}
        {clients.length === 0 && <p className="text-sm text-slate-400 col-span-full text-center py-10">No clients yet. Create your first client.</p>}
      </div>
    </div>
  );
}
